/**
 * PDCore — геометрия межзрачкового расстояния для /pupillary-distance.
 *
 * Чистая математика без DOM: лендмарки MediaPipe (0..1) → пиксели кадра →
 * миллиметры. Живёт отдельным файлом, чтобы её можно было проверить в Node
 * (tests/js/pupillary-distance-core.test.mjs) — прибор, который показывает
 * человеку число для заказа очков, не имеет права держать формулы там, где их
 * нельзя прогнать на известных ответах.
 *
 * Три решения, каждое по замеру, а не по вкусу:
 *
 * 1. СТОРОНЫ. Индексы 468–472 — ПРАВЫЙ глаз человека, 473–477 — левый
 *    (проверено на фронтальном портрете: 468 лежит в левой половине
 *    незеркального кадра). Прежняя версия тула подписывала 468 «левым», то
 *    есть монокулярные PD — ровно то, ради чего их спрашивают для прогрессивных
 *    линз, — выдавались перепутанными.
 *
 * 2. РАДУЖКА — ЭЛЛИПС. MediaPipe отдаёт контур, у которого вертикальный
 *    диаметр на ~10 % меньше горизонтального (замер: 45.1/40.8 и 46.8/41.4 px),
 *    как у настоящей роговицы (горизонтальный видимый диаметр ≈ 11.7 мм,
 *    вертикальный ≈ 10.6). Эталон 11.7 мм — ГОРИЗОНТАЛЬНЫЙ, поэтому масштаб
 *    берётся по горизонтальной паре точек. Прежнее среднее по четырём радиусам
 *    занижало масштаб и завышало PD примерно на 5 % (~3 мм).
 *
 * 3. КОНВЕРГЕНЦИЯ. Глядя в камеру на 40–50 см, глаза сводятся, и зрачки
 *    сближаются. Очкам нужно PD «для дали» — поэтому измеренное значение
 *    разводится обратно по геометрии центров вращения глаз (convergenceFactor).
 *    Без этой поправки любой замер «в камеру» занижает PD на 1–2 мм.
 */
(function (root) {
    'use strict';

    var C = {
        // Горизонтальный видимый диаметр радужки взрослого (white-to-white).
        HVID_MM: 11.7,
        // 1σ популяционного разброса HVID (≈ 3.6 % от среднего).
        HVID_SD_MM: 0.42,
        // Длинная грань карты ISO/IEC 7810 ID-1 (банковская, права, пропуск).
        CARD_MM: 85.6,
        // Центр вращения глаза за входным зрачком, мм. Учебник даёт 13.5 мм от
        // вершины роговицы минус ~3 до зрачка (10.5); прямой замер
        // горизонтального вращения по смещению зрачка — 15.3 − 3.6 = 11.7
        // (Ohlendorf 2022). Берём середину: разница между ними — 0.2 мм PD.
        COR_BEHIND_PUPIL_MM: 11.5,
        // Центр вращения глаза за плоскостью очковой линзы, мм (Jalie, ABDO:
        // NCD = PD · l/(l + 27)). Отсюда «для близи на 40 см — минус 4 мм», а не
        // расхожие «минус 3», которые верны лишь для 50–60 см.
        COR_TO_SPECTACLE_MM: 27,
        // Лоб, на котором лежит карта, стоит ближе к камере, чем зрачки:
        // середина лба в канонической модели MediaPipe на 11 мм впереди века,
        // плюс ~3 мм от роговицы до зрачка.
        CARD_DEPTH_MM: 14,
        CARD_DEPTH_SD_MM: 3,
        // Горизонтальный угол обзора по ДЛИННОЙ стороне кадра, когда устройство
        // его не сообщает (веб-камеры ноутбуков 55–78° по диагонали, фронтальные
        // камеры телефонов 80–86°). Вся поправка на конвергенцию — около 3 %,
        // поэтому ошибка угла на ±20 % сдвигает PD лишь на ~0.4 мм.
        HFOV_DEG: 68,
        HFOV_PHONE_DEG: 74,
        HFOV_DESK_DEG: 62,
        // Нормальный диапазон PD взрослого, мм — за его пределами просим проверить.
        ADULT_MIN: 50,
        ADULT_MAX: 78,
    };

    // Индексы лендмарков FaceLandmarker (478 точек с радужкой).
    var IDX = {
        R_PUPIL: 468, R_IRIS: [469, 470, 471, 472],
        L_PUPIL: 473, L_IRIS: [474, 475, 476, 477],
        R_OUTER: 33, R_INNER: 133, R_UP: 159, R_LOW: 145,
        L_OUTER: 263, L_INNER: 362, L_UP: 386, L_LOW: 374,
        BRIDGE: 168, BROW_MID: 9, FOREHEAD: 151,
        R_BROW_TOP: 105, L_BROW_TOP: 334,
    };

    function sub(a, b) { return { x: a.x - b.x, y: a.y - b.y }; }
    function dot(a, b) { return a.x * b.x + a.y * b.y; }
    function len(a) { return Math.hypot(a.x, a.y); }
    function dist(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }
    function mid(a, b) { return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; }
    function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
    var DEG = 180 / Math.PI;

    /**
     * Горизонтальный и вертикальный диаметры радужки по четырём точкам края.
     * Какая пара «горизонтальная», решает направление межзрачковой оси, а не
     * порядок индексов: при наклоне головы порядок остаётся, а горизонталь нет.
     */
    function irisDiameters(P, ring, axis) {
        var a = dist(P(ring[0]), P(ring[2])), b = dist(P(ring[1]), P(ring[3]));
        var va = sub(P(ring[2]), P(ring[0])), vb = sub(P(ring[3]), P(ring[1]));
        var ca = Math.abs(dot(va, axis)) / (len(va) || 1);
        var cb = Math.abs(dot(vb, axis)) / (len(vb) || 1);
        return ca >= cb ? { h: a, v: b } : { h: b, v: a };
    }

    /**
     * Поза головы из матрицы FaceLandmarker (column-major 4×4). Направление
     * «вперёд» лица — третий столбец поворота; рыскание и тангаж — его углы.
     */
    function poseFromMatrix(m) {
        if (!m || m.length < 16) return null;
        var fx = m[8], fy = m[9], fz = m[10];
        if (!isFinite(fx) || !isFinite(fz) || Math.abs(fz) < 1e-6) return null;
        return {
            yaw: Math.atan2(fx, fz) * DEG,
            pitch: Math.atan2(fy, fz) * DEG,
        };
    }

    /**
     * Всё, что нужно от одного кадра, в пикселях этого кадра.
     * pts — нормализованные точки MediaPipe; W/H — размер кадра; m — матрица позы.
     */
    function frameMetrics(pts, W, H, m) {
        if (!pts || pts.length < 478) return null;
        var P = function (i) { return { x: pts[i].x * W, y: pts[i].y * H, z: (pts[i].z || 0) * W }; };
        var rP = P(IDX.R_PUPIL), lP = P(IDX.L_PUPIL);
        var ipdPx = dist(rP, lP);
        if (!(ipdPx > 1)) return null;
        var u = { x: (lP.x - rP.x) / ipdPx, y: (lP.y - rP.y) / ipdPx };

        var rI = irisDiameters(P, IDX.R_IRIS, u), lI = irisDiameters(P, IDX.L_IRIS, u);
        var irisH = (rI.h + lI.h) / 2;

        // Середина переносицы = середина между внутренними уголками глаз: она на
        // той же глубине, что и зрачки, поэтому при повороте головы смещается
        // вместе с ними (точка на спинке носа выступает вперёд и уезжает).
        var ri = P(IDX.R_INNER), li = P(IDX.L_INNER);
        var m0 = mid(ri, li);
        var monoR = dot(sub(m0, rP), u), monoL = dot(sub(lP, m0), u);

        // Раскрытие глаз (веко к ширине глаза) — моргнувший кадр не считается.
        var ro = P(IDX.R_OUTER), lo = P(IDX.L_OUTER);
        var openR = dist(P(IDX.R_UP), P(IDX.R_LOW)) / (dist(ro, ri) || 1);
        var openL = dist(P(IDX.L_UP), P(IDX.L_LOW)) / (dist(lo, li) || 1);

        // Куда смотрит глаз по горизонтали: доля пути зрачка от внешнего уголка
        // к внутреннему. Взгляд в камеру даёт почти одинаковые доли у обоих
        // глаз; взгляд вбок — расходящиеся.
        var gR = dot(sub(rP, ro), u) / (dot(sub(ri, ro), u) || 1);
        var gL = dot(sub(lo, lP), u) / (dot(sub(lo, li), u) || 1);

        var roll = Math.atan2(u.y, u.x) * DEG;
        var pose = poseFromMatrix(m);
        var yaw, pitch;
        if (pose) { yaw = pose.yaw; pitch = pose.pitch; }
        else {
            // Без матрицы: рыскание по асимметрии видимой ширины глаз.
            var wr = dist(ro, ri), wl = dist(lo, li);
            yaw = Math.asin(clamp((wl - wr) / (wl + wr) * 2.2, -1, 1)) * DEG;
            pitch = 0;
        }

        // Лоб — место для карты. Центр между бровями и направление межзрачковой оси.
        var brow = mid(P(IDX.R_BROW_TOP), P(IDX.L_BROW_TOP));

        return {
            W: W, H: H, rP: rP, lP: lP, u: u, ipdPx: ipdPx,
            irisH: irisH, irisV: (rI.v + lI.v) / 2, irisR: rI.h, irisL: lI.h,
            mid: m0, monoR: monoR, monoL: monoL,
            openR: openR, openL: openL, gazeR: gR, gazeL: gL,
            roll: roll, yaw: yaw, pitch: pitch, brow: brow,
            faceW: dist(P(234), P(454)),
        };
    }

    /** Фокус в пикселях при известном (или принятом) горизонтальном угле обзора. */
    function focalPx(W, H, hfovDeg) {
        var long = Math.max(W, H);
        return (long / 2) / Math.tan(((hfovDeg || C.HFOV_DEG) / 2) / DEG);
    }

    /** Расстояние от камеры до глаз, мм — по видимому размеру радужки. */
    function distanceMm(fm, hfovDeg) {
        if (!fm || !(fm.irisH > 0)) return null;
        return focalPx(fm.W, fm.H, hfovDeg) * C.HVID_MM / fm.irisH;
    }

    /**
     * Во сколько раз PD «для дали» больше измеренного при взгляде в камеру на
     * расстоянии D (мм, от камеры до зрачков). Зрачок лежит на прямой от центра
     * вращения глаза к точке фиксации, поэтому x_зрачка = x_ЦВ · D/(D + r).
     */
    function convergenceFactor(D) {
        if (!(D > 50)) return 1;
        return (D + C.COR_BEHIND_PUPIL_MM) / D;
    }

    /**
     * PD для близи (межцентровое расстояние линз для чтения) из PD для дали.
     * L — расстояние чтения от плоскости очков, мм. Линия взгляда идёт от центра
     * вращения к тексту и пересекает плоскость линзы на доле L/(L + s).
     */
    function nearFactor(L) {
        if (!(L > 100)) return 1;
        return L / (L + C.COR_TO_SPECTACLE_MM);
    }

    /** Карта у лба ближе к камере на δ, поэтому её масштаб крупнее масштаба глаз. */
    function cardDepthFactor(D, depthMm) {
        var d = depthMm == null ? C.CARD_DEPTH_MM : depthMm;
        if (!(D > d + 50)) return 1;
        return D / (D - d);
    }

    function median(a) {
        if (!a.length) return NaN;
        var s = a.slice().sort(function (x, y) { return x - y; });
        var n = s.length, h = n >> 1;
        return n % 2 ? s[h] : (s[h - 1] + s[h]) / 2;
    }
    function mad(a, m) {
        if (!a.length) return NaN;
        if (m == null) m = median(a);
        return median(a.map(function (v) { return Math.abs(v - m); }));
    }

    /**
     * Проверки кадра: годится ли он в замер и что сказать человеку.
     * Возвращает { ok, issue } — первая по важности проблема.
     */
    function frameQuality(fm, opts) {
        opts = opts || {};
        if (!fm) return { ok: false, issue: 'no_face' };
        var D = distanceMm(fm, opts.hfov);
        var tooFar = opts.farMm || 900, tooNear = opts.nearMm || 250;
        var issue = null;
        // Порядок — по важности для человека: сначала то, что делает замер
        // невозможным, потом то, что делает его неточным.
        if (Math.min(fm.openR, fm.openL) < 0.16) issue = 'blink';
        else if (D && D > tooFar) issue = 'closer';
        else if (fm.irisH < (opts.minIrisPx || 14)) issue = 'closer';
        else if (D && D < tooNear) issue = 'farther';
        else if (Math.abs(fm.yaw) > (opts.maxYaw || 10)) issue = 'turn';
        else if (Math.abs(fm.roll) > (opts.maxRoll || 7)) issue = 'tilt';
        else if (Math.abs(fm.pitch) > (opts.maxPitch || 18)) issue = 'chin';
        else if (Math.abs(fm.gazeR - fm.gazeL) > (opts.maxGaze || 0.2)) issue = 'gaze';
        else if (Math.abs(fm.irisR - fm.irisL) / (fm.irisH || 1) > (opts.maxIrisAsym || 0.14)) issue = 'turn';
        return { ok: !issue, issue: issue, distanceMm: D };
    }

    /**
     * Сборка результата из (медианных) пиксельных величин кадра.
     *   s = { ipdPx, monoR, monoL, irisH, W, H, D?, scale: 'iris'|'card'|'personal',
     *         cardPx?, cardDepthMm?, hvidMm?, noiseMm?, hfov? }
     */
    function compute(s) {
        var D = s.D || distanceMm({ W: s.W, H: s.H, irisH: s.irisH }, s.hfov) || 450;
        // Погрешность — из двух частей. СИСТЕМАТИЧЕСКАЯ одинакова во всех кадрах
        // одного замера (разброс радужки у людей, глубина карты, её наклон), и
        // медиана кадров её не уменьшает. СЛУЧАЙНАЯ — точность точек в кадре:
        // она делится на корень из числа независимых кадров.
        var pxPerMm, sysRel, randRel;
        var nF = Math.max(1, s.frames || 1);
        if (s.scale === 'card' && s.cardPx > 0) {
            // Масштаб карты переносится в плоскость зрачков поправкой глубины.
            pxPerMm = s.cardPx / C.CARD_MM / cardDepthFactor(D, s.cardDepthMm);
            var dSd = (s.cardDepthSdMm == null ? C.CARD_DEPTH_SD_MM : s.cardDepthSdMm) / D;
            // 0.7 % — карта, лежащая на лбу не идеально ровно (поворот на 7°);
            // и дистанция D сама известна лишь ±20 % (угол обзора камеры
            // браузер не сообщает) — это ошибка поправки глубины δ/D.
            var dD = (s.cardDepthMm == null ? C.CARD_DEPTH_MM : s.cardDepthMm) / D * 0.2;
            sysRel = Math.sqrt(dSd * dSd + dD * dD + 0.007 * 0.007);
            randRel = Math.sqrt(Math.pow(1.6 / s.cardPx, 2) * 2 + Math.pow(1.0 / s.ipdPx, 2));
        } else {
            var hv = s.scale === 'personal' && s.hvidMm > 0 ? s.hvidMm : C.HVID_MM;
            pxPerMm = s.irisH / hv;
            sysRel = s.scale === 'personal' ? (s.hvidSdRel || 0.015) : C.HVID_SD_MM / C.HVID_MM;
            // радужка — десятки пикселей: пиксель ошибки у одного кадра заметен,
            // у медианы многих кадров — вдвое меньше
            randRel = (nF >= 10 ? 0.6 : 0.9) / s.irisH;
            nF = 1;
        }
        var k = convergenceFactor(D);
        // Поворот головы. Карта лежит на лбу и поворачивается вместе с ним —
        // её сжатие и сжатие межзрачкового отрезка взаимно гасятся. Радужка
        // так не умеет: глаза доворачиваются к камере, радужка остаётся
        // «анфас», а отрезок между зрачками сжимается на cos(поворота).
        var yawK = s.scale === 'card' ? 1 : 1 / Math.cos(clamp(Math.abs(s.yaw || 0), 0, 20) / DEG);
        var total = (s.ipdPx / pxPerMm) * k * yawK;
        var share = s.monoR + s.monoL > 0 ? s.monoR / (s.monoR + s.monoL) : 0.5;
        var R = total * share + (s.noYawMono ? 0 : monoYawShiftMm(s.yaw)), L = total - R;
        var sysMm = Math.sqrt(Math.pow(total * sysRel, 2) + Math.pow(total * (k - 1) * 0.3, 2));
        var randMm = total * randRel;
        var sd = Math.sqrt(sysMm * sysMm + randMm * randMm / nF + Math.pow(s.noiseMm || 0, 2));
        return {
            pd: total, R: R, L: L, sd: sd, pm: plusMinus(sd), sysMm: sysMm, randMm: randMm,
            D: D, conv: k, pxPerMm: pxPerMm, scale: s.scale || 'iris',
            // Масштаб, который дала бы радужка при этом масштабе карты: так
            // запоминается «своя» радужка для быстрых замеров без карты.
            hvidMeasured: s.irisH > 0 ? s.irisH / pxPerMm : null,
        };
    }

    /**
     * Поворот головы переносит монокулярное PD с одной стороны на другую.
     * Взгляд держится на камере, значит глаза доворачиваются относительно
     * головы на угол поворота, и оба зрачка сдвигаются в глазницах В ОДНУ
     * сторону — на (центр вращения → зрачок) · sin(поворота), около 0.18 мм
     * на градус. Середина переносицы (между внутренними уголками глаз) при
     * этом стоит на месте. Знак: матрица MediaPipe построена по незеркальному
     * кадру; нос смотрит в −x (к левому краю кадра, к правому глазу
     * человека) → зрачки уходят к правому краю кадра → правое PD читается
     * меньше, левое больше. Возвращает поправку к доле правого глаза.
     */
    function monoYawShiftMm(yawDeg) {
        return -Math.sin(clamp(yawDeg || 0, -15, 15) / DEG) * C.COR_BEHIND_PUPIL_MM;
    }

    /** Округление, принятое у оптиков: до 0.5 мм. */
    function roundHalf(v) { return Math.round(v * 2) / 2; }

    /** «±» для человека: 1.5σ с шагом 0.5 мм и не меньше 0.5. */
    function plusMinus(sd) { return Math.max(0.5, Math.ceil(sd * 1.5 * 2) / 2); }

    /** PD для близи и монокулярные значения для чтения. */
    function nearFrom(r, readingMm) {
        var f = nearFactor(readingMm);
        return { pd: r.pd * f, R: r.R * f, L: r.L * f, factor: f };
    }

    /**
     * Ярлык готовых очков (РЦ/PD на дужке): ближайший стандартный интервал в
     * 2 мм. Готовые очки продаются шагом 2 мм («58–60», «62–64»), и значение,
     * попавшее на границу, честно получает оба соседних интервала.
     */
    function readyBins(pd) {
        if (!(pd > 0)) return [];
        var lo = Math.floor(pd / 2) * 2, out = [];
        if (Math.abs(pd - lo) < 0.26 && lo > 0) out.push([lo - 2, lo]);
        out.push([lo, lo + 2]);
        if (Math.abs(pd - (lo + 2)) < 0.26) out.push([lo + 2, lo + 4]);
        return out;
    }

    /**
     * Какой интервал брать у готовых очков. Для чтения — по PD ДЛЯ БЛИЗИ, и
     * между двумя интервалами — меньший: у плюсовых линз слишком широкие
     * центры дают призму «основанием наружу», которую глазам сводить труднее
     * (так советуют оптики; ГОСТ Р 51193 маркировку РЦ не нормирует).
     * Для дали — интервал, в который попало PD для дали.
     */
    function readyPick(pd, kind) {
        var bins = readyBins(pd);
        if (!bins.length) return null;
        return kind === 'reading' ? bins[0] : bins[bins.length > 1 ? 1 : 0];
    }

    /**
     * Установка IPD в VR-шлемах. Значения диапазонов и позиций — по
     * документации производителей (см. pd-core тест: таблица сверена).
     */
    var VR = [
        // Колёсико линз 58–70 мм; ПО поддерживает 53–75 (UploadVR, спецификация).
        { id: 'quest3', name: 'Meta Quest 3', kind: 'range', min: 58, max: 70 },
        // Пороги — из справки Meta: <61 → 1 (58 мм), 61–66 → 2 (63), >66 → 3 (68).
        { id: 'quest3s', name: 'Meta Quest 3S / Quest 2', kind: 'steps', steps: [58, 63, 68], cuts: [61, 66] },
        { id: 'questpro', name: 'Meta Quest Pro', kind: 'range', min: 55, max: 75 },
        { id: 'pico4u', name: 'Pico 4 Ultra', kind: 'range', min: 58, max: 72 },
        { id: 'pico4', name: 'Pico 4', kind: 'range', min: 62, max: 72 },
        // Колёсико без шкалы: шлем сам ведёт по отслеживанию глаз.
        { id: 'psvr2', name: 'PlayStation VR2', kind: 'guided', min: 59, max: 72 },
        { id: 'index', name: 'Valve Index', kind: 'range', min: 58, max: 70 },
        { id: 'vive', name: 'HTC Vive Pro 2', kind: 'range', min: 57, max: 70 },
        { id: 'focus3', name: 'HTC Vive Focus 3', kind: 'range', min: 57, max: 72 },
        { id: 'avp', name: 'Apple Vision Pro', kind: 'auto', min: 51, max: 75 },
    ];
    function vrSettings(pd) {
        return VR.map(function (h) {
            var o = { id: h.id, name: h.name, kind: h.kind };
            if (h.kind === 'range') {
                o.value = roundHalf(clamp(pd, h.min, h.max));
                o.clamped = pd < h.min ? 'min' : pd > h.max ? 'max' : null;
                o.min = h.min; o.max = h.max;
            } else if (h.kind === 'guided' || h.kind === 'auto') {
                o.min = h.min; o.max = h.max;
                o.clamped = pd < h.min ? 'min' : pd > h.max ? 'max' : null;
            } else if (h.kind === 'steps') {
                var best = 0;
                if (h.cuts) best = pd < h.cuts[0] ? 0 : pd <= h.cuts[1] ? 1 : 2;
                else for (var i = 1; i < h.steps.length; i++) if (Math.abs(h.steps[i] - pd) < Math.abs(h.steps[best] - pd)) best = i;
                o.step = best + 1; o.value = h.steps[best]; o.steps = h.steps;
            }
            return o;
        });
    }

    /** Итог нескольких замеров одного человека: медиана и разброс. */
    function combine(results) {
        var ok = results.filter(function (r) { return r && r.pd > 0; });
        if (!ok.length) return null;
        var pds = ok.map(function (r) { return r.pd; });
        var m = median(pds);
        var share = median(ok.map(function (r) { return r.R / r.pd; }));
        var spread = ok.length > 1 ? Math.max.apply(null, pds) - Math.min.apply(null, pds) : 0;
        var sdMean = median(ok.map(function (r) { return r.sd; })) / Math.sqrt(Math.max(1, ok.length) / 1.6);
        return { pd: m, R: m * share, L: m * (1 - share), n: ok.length, spread: spread, sd: sdMean, pm: plusMinus(sdMean) };
    }

    /** Разбор PD, записанного в рецепте: «63», «63/60», «31.5/32», «OD 31 OS 32». */
    function parsePrescription(text) {
        if (!text) return null;
        var s = String(text).replace(/,/g, '.').replace(/мм|mm/gi, ' ');
        var nums = (s.match(/\d+(?:\.\d+)?/g) || []).map(Number).filter(function (n) { return n >= 20 && n <= 90; });
        if (!nums.length) return null;
        if (nums.length === 1) return { pd: nums[0] };
        var a = nums[0], b = nums[1];
        // Две половины (≈25–40 мм каждая) — монокулярные; два целых PD — даль/близь.
        if (a < 45 && b < 45) return { pd: a + b, R: a, L: b, mono: true };
        if (a >= 45 && b >= 45) return { pd: Math.max(a, b), near: Math.min(a, b), pair: true };
        return { pd: Math.max(a, b) };
    }

    var API = {
        C: C, IDX: IDX, frameMetrics: frameMetrics, poseFromMatrix: poseFromMatrix,
        focalPx: focalPx, distanceMm: distanceMm, convergenceFactor: convergenceFactor,
        nearFactor: nearFactor, cardDepthFactor: cardDepthFactor, frameQuality: frameQuality,
        compute: compute, nearFrom: nearFrom, readyBins: readyBins, readyPick: readyPick, vrSettings: vrSettings,
        combine: combine, parsePrescription: parsePrescription, roundHalf: roundHalf,
        plusMinus: plusMinus, median: median, mad: mad, VR: VR, monoYawShiftMm: monoYawShiftMm,
    };
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) root.PDCore = API;
})(typeof window !== 'undefined' ? window : null);
